import { useEffect, useState } from "react";
import { api, Connection, getActiveBook, Manifest } from "../api";
import { usdCompact } from "../lib/money";

// FIRM-WIDE money plumbing: every allocation dial and every venue balance in
// ONE place. Dials are config — they apply to all books; dollar previews use
// the ACTIVE book's account so paper previews paper and live previews live.
type Alloc = {
  long_term_pct: number; short_term_pct: number; account_equity: number;
  short_crypto_pct?: number; growth_crypto_pct?: number;
  crypto_venue?: string; crypto_available_cash?: number;
  forex_enabled?: boolean; short_forex_pct?: number; growth_forex_pct?: number;
  forex_venue?: string; forex_available_cash?: number;
  commodities_enabled?: boolean; commodities_alpaca_pct?: number;
};
type Holdings = { allocation: Alloc };

const usd = usdCompact; // dollar previews are big-tile numbers — whole dollars

// The chip in the frame says FIRM-WIDE (the dials ARE firm config), but the
// dollar previews use the ACTIVE book's account — name it explicitly so the
// chip and the dollars stop disagreeing (Kaizen D4/F5).
const BOOK_NAME: Record<string, string> = {
  paper: "Paper · Daily", live: "Live · Daily",
  paper_long_term: "Paper · Growth", live_long_term: "Live · Growth",
};

export default function Funding(_: { manifest: Manifest }) {
  const [a, setA] = useState<Alloc | null>(null);
  const [conns, setConns] = useState<Connection[]>([]);
  const [ltPct, setLtPct] = useState<number | null>(null);
  const [cShort, setCShort] = useState<number | null>(null);
  const [cGrowth, setCGrowth] = useState<number | null>(null);
  const [fShort, setFShort] = useState<number | null>(null);
  const [fGrowth, setFGrowth] = useState<number | null>(null);
  const [comPct, setComPct] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Poll — venue balances used to freeze until a manual re-navigation
  // (Kaizen D5/F7). Safe with the sliders: load() only fills unset values.
  useEffect(() => {
    void load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function load() {
    try {
      const h = await api.get<Holdings>("/api/longterm/holdings");
      setA(h.allocation);
      setLtPct((p) => p ?? h.allocation.long_term_pct);
      setCShort((p) => p ?? h.allocation.short_crypto_pct ?? 50);
      setCGrowth((p) => p ?? h.allocation.growth_crypto_pct ?? 30);
      setFShort((p) => p ?? h.allocation.short_forex_pct ?? 60);
      setFGrowth((p) => p ?? h.allocation.growth_forex_pct ?? 20);
      setComPct((p) => p ?? h.allocation.commodities_alpaca_pct ?? 0);
      setErr(null);
    } catch (e) { setErr((e as Error).message); }
    api.get<Connection[]>("/api/connections").then(setConns).catch(() => {});
  }
  async function save(kind: "equity" | "crypto" | "forex" | "commodities") {
    setBusy(kind); setErr(null);
    try {
      if (kind === "equity") await api.post("/api/longterm/allocation", { long_term_pct: ltPct });
      if (kind === "crypto") await api.post("/api/crypto/allocation", { short_pct: cShort, growth_pct: cGrowth });
      if (kind === "forex") await api.post("/api/forex/allocation", { short_pct: fShort, growth_pct: fGrowth });
      if (kind === "commodities") await api.post("/api/commodities/allocation", { alpaca_pct: comPct });
      await load();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  if (!a || ltPct === null) return <div className="text-slate-500 text-sm">{err ?? "loading…"}</div>;
  const eq = a.account_equity;
  const shortEq = eq * (100 - ltPct) / 100, growthEq = eq * ltPct / 100;
  const fxBuffer = Math.round((100 - (fShort ?? 0) - (fGrowth ?? 0)) * 10) / 10;
  const cxBuffer = Math.round((100 - (cShort ?? 0) - (cGrowth ?? 0)) * 10) / 10;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h1 className="text-lg font-bold">Funding &amp; Dials</h1>
        <span className="text-xs text-slate-500">
          every dial and every venue balance — dials are firm-wide; dollar previews use the{" "}
          <b className="text-slate-300">{BOOK_NAME[getActiveBook()] ?? getActiveBook()}</b> book&rsquo;s account
        </span>
      </div>
      {err && <div className="text-down text-sm">{err}</div>}

      {/* Venue balances */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Venue name="Alpaca (equities)" v={usd(eq)}
               sub={`${BOOK_NAME[getActiveBook()] ?? getActiveBook()} — ${getActiveBook().startsWith("live") ? "REAL money" : "PAPER, simulated"}`} />
        <Venue name={`${(a.crypto_venue ?? "alpaca").toUpperCase()} (crypto)`}
               v={a.crypto_available_cash != null ? usd(a.crypto_available_cash) : "—"}
               sub="REAL money · live books only — paper simulates on Alpaca paper" />
        <Venue name={`${(a.forex_venue ?? "oanda").toUpperCase()} (forex)`}
               v={a.forex_available_cash != null ? usd(a.forex_available_cash) : "—"}
               sub={a.forex_enabled ? "REAL money · live books only" : "forex disabled"} />
        <div className="card py-2">
          <div className="text-xs text-slate-500 mb-1">Connections</div>
          <div className="flex flex-wrap gap-1">
            {conns.map((c) => (
              <span key={c.kind} title={c.health_msg ?? ""}
                    className={`badge ${c.health === "ok" ? "bg-up/20 text-up" : "bg-down/20 text-down"}`}>
                {c.kind}
              </span>
            ))}
            {conns.length === 0 && <span className="text-slate-600 text-xs">—</span>}
          </div>
        </div>
      </div>

      {/* Equity: the top split */}
      <Dial title="Equity — short-term vs growth" busyKey="equity" busy={busy}
            dirty={ltPct !== a.long_term_pct} onSave={() => save("equity")}
            note="The top split of the account. Starts short-term-heavy; Stonky shifts weight to growth as the firm earns it.">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-up font-semibold">{100 - ltPct}% short-term · {usd(shortEq)}</span>
          <span className="text-gold font-semibold">{ltPct}% growth · {usd(growthEq)}</span>
        </div>
        <input type="range" min={10} max={60} step={1} value={ltPct}
               onChange={(e) => setLtPct(Number(e.target.value))} className="w-full accent-gold" />
      </Dial>

      {/* Commodities: the ONLY sleeve that shares the Alpaca account, so its
          dial is a carve-out of the equity split above rather than a share of
          a separate venue balance. Sits directly under Equity for that reason
          — the two numbers are the same pot. */}
      <Dial title="Commodities — carve-out of the Alpaca balance" busyKey="commodities" busy={busy}
            dirty={comPct !== (a.commodities_alpaca_pct ?? 0)}
            onSave={() => save("commodities")}
            note={a.commodities_enabled
              ? "Live: this much of the Alpaca balance is reserved for commodities and is NOT available to the equity desk."
              : "Reserved, not withheld — the commodities sleeve is not enabled, so the equity desk still has the full balance. Turning the sleeve on is what makes this bite."}>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-up font-semibold">
            {(100 - (comPct ?? 0)).toFixed(0)}% equities · {usd(eq * (100 - (comPct ?? 0)) / 100)}
          </span>
          <span className={`font-semibold ${a.commodities_enabled ? "text-gold" : "text-slate-500"}`}>
            {(comPct ?? 0).toFixed(0)}% commodities · {usd(eq * (comPct ?? 0) / 100)}
            {!a.commodities_enabled && <span className="text-slate-600 font-normal"> (reserved)</span>}
          </span>
        </div>
        <input type="range" min={0} max={50} step={1} value={comPct ?? 0}
               onChange={(e) => setComPct(Number(e.target.value))}
               className={`w-full ${a.commodities_enabled ? "accent-gold" : "accent-slate-500"}`} />
        <div className="text-[10px] text-slate-600 mt-1">
          Split across both books by the equity dial above: daily{" "}
          {usd(eq * (100 - ltPct) / 100 * (comPct ?? 0) / 100)} · growth{" "}
          {usd(eq * ltPct / 100 * (comPct ?? 0) / 100)}
        </div>
      </Dial>

      {/* Crypto: split of its OWN venue balance — deliberately the SAME shape,
          labels and denominator as forex below. It used to be a "carve-out of
          each book's sleeve" (a % of the Alpaca equity pot), so the two
          sleeves described their dollars in different units and the Kraken
          balance the live trades actually settle against wasn't what sized
          them at all. */}
      <Dial title={`Crypto — split of the ${(a.crypto_venue ?? "alpaca").toUpperCase()} balance`}
            busyKey="crypto" busy={busy}
            dirty={cShort !== (a.short_crypto_pct ?? 50) || cGrowth !== (a.growth_crypto_pct ?? 30)}
            onSave={cxBuffer >= 0 ? () => save("crypto") : undefined}
            note={`Funded by its own ${usd(a.crypto_available_cash ?? 0)} balance — never the equity above. Held 24/7, never flattened. The remainder stays uncommitted. Live books trade on ${(a.crypto_venue ?? "alpaca").toUpperCase()}; paper books simulate on Alpaca paper.`}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Slider label="daily book" pct={cShort ?? 50}
                  amount={usd((a.crypto_available_cash ?? 0) * (cShort ?? 50) / 100)}
                  tone="accent" onChange={setCShort} />
          <Slider label="growth book" pct={cGrowth ?? 30}
                  amount={usd((a.crypto_available_cash ?? 0) * (cGrowth ?? 30) / 100)}
                  tone="gold" onChange={setCGrowth} />
          <BufferBar pct={cxBuffer} label="uncommitted buffer" />
        </div>
      </Dial>

      {/* Forex: split of the OANDA balance */}
      <Dial title={`Forex — split of the ${(a.forex_venue ?? "oanda").toUpperCase()} balance`} busyKey="forex" busy={busy}
            dirty={fShort !== (a.short_forex_pct ?? 60) || fGrowth !== (a.growth_forex_pct ?? 20)}
            onSave={fxBuffer >= 0 ? () => save("forex") : undefined}
            note={`Funded by its own ${usd(a.forex_available_cash ?? 0)} balance — never the equity above. The remainder is an uncommitted margin buffer (FX is leveraged and gaps).`}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Slider label="daily book" pct={fShort ?? 60} amount={usd((a.forex_available_cash ?? 0) * (fShort ?? 60) / 100)}
                  tone="accent" onChange={setFShort} />
          <Slider label="growth book" pct={fGrowth ?? 20} amount={usd((a.forex_available_cash ?? 0) * (fGrowth ?? 20) / 100)}
                  tone="gold" onChange={setFGrowth} />
          <BufferBar pct={fxBuffer} label="margin buffer" />
        </div>
      </Dial>
    </div>
  );
}

function Venue({ name, v, sub }: { name: string; v: string; sub: string }) {
  return (
    <div className="card py-2">
      <div className="text-xs text-slate-500">{name}</div>
      <div className="text-lg font-bold font-mono">{v}</div>
      <div className="text-[10px] text-slate-600">{sub}</div>
    </div>
  );
}

function Dial({ title, note, dirty, busy, busyKey, onSave, children }: {
  title: string; note: string; dirty: boolean; busy: string | null; busyKey: string;
  onSave?: () => void; children: React.ReactNode;
}) {
  return (
    <div className="card space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="label mb-0">{title}</h2>
        {dirty && onSave && (
          <button className="btn btn-primary text-xs ml-auto" onClick={onSave} disabled={busy === busyKey}>
            {busy === busyKey ? "…" : "Set"}
          </button>
        )}
      </div>
      {children}
      <p className="text-[11px] text-slate-600">{note}</p>
    </div>
  );
}

function Slider({ label, pct, amount, tone, onChange }: {
  label: string; pct: number; amount: string; tone: "accent" | "gold"; onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400">{label}</span>
        <span className={`font-semibold ${tone === "gold" ? "text-gold" : "text-accent"}`}>{pct}% · {amount}</span>
      </div>
      <input type="range" min={0} max={100} step={1} value={pct}
             onChange={(e) => onChange(Number(e.target.value))}
             className={`w-full ${tone === "gold" ? "accent-gold" : "accent-accent"}`} />
    </div>
  );
}

// The uncommitted remainder of a venue balance. Shared by crypto and forex so
// both sleeves show their split the same way — three parts that always add to
// 100%, whatever the balance happens to be today.
function BufferBar({ pct, label }: { pct: number; label: string }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400">{label}</span>
        <span className={pct < 0 ? "text-down font-semibold" : "text-slate-300 font-semibold"}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded bg-panel overflow-hidden mt-2.5">
        <div className={`h-full ${pct < 0 ? "bg-down" : "bg-slate-600"}`}
             style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      {pct < 0 && <div className="text-down text-[11px] mt-1">over 100% — reduce one</div>}
    </div>
  );
}
